import React, { Component } from 'react';
import { TransactionJsonModel } from '../../../../domain/stream/transaction/TransactionDomains';
import { RootState } from '../../../../redux/store';
import { AiModelState } from '../../../../redux/reducers/AiModelReducer';
import { changeAiModel } from '../../../../redux/actions/AiModelActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { FormicaProps, withFormica } from '../../../../helpers/withFormica';
import { InstanceContainer } from '../../../../config/binding/InstanceBinding';
import Injectables from '../../../../constant/Injectables';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../../../constant/CollectionNames';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../../../custom/FormicaSearchSelectBox';
import DataType from '../../../../constant/DataType';
import SubscriptionContext = subscribe.SubscriptionContext;
import QueryableRepository = repository.QueryableRepository;
import QueryBuilder = query.QueryBuilder;
import AiModelOriginalFeaturesComponent from './AiModelOriginalFeaturesComponent';
import AiModelCustomFeaturesComponent from './AiModelCustomFeaturesComponent';
import ValueFactory from '../../../../util/ValueFactory';
import { MLOpsModelBaseFeature, MLOpsModelCustomFeature } from '../../../../domain/mlops/MLOpsDomains';
import TableColumnPreferencesComponent from '../../../TableColumnPreferencesComponent';
import RoleHelper from '../../../../RoleHelper';
import ComponentName from '../../../../constant/ComponentName';
import AiModelTrainingParameterComponent from '../AiModelTrainingParameterComponent';
import FilterUtil from '../../../../util/FilterUtil';
import or = query.or;
import eq = query.eq;
import VariableType from '../../../../constant/VariableType';
import Subscription = subscribe.Subscription;
import and = query.and;
import CustomLoading from '../../../custom/CustomLoading';
import LanguageUtil from '../../../../util/LanguageUtil';

class AiModelFeaturesComponent extends Component<Props, State> {
  public state: State = new State();

  private readonly subscriptionContext: SubscriptionContext = InstanceContainer.get(Injectables.SUBSCRIPTION_CONTEXT);

  private enrichmentSubscription: Subscription | undefined;

  private readonly repository: QueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private computation: subscribe.Computation | undefined;

  public render() {
    //todo
    const disabled = false;
    const { selectedVariables, loading, update } = this.state;
    const {
      aiModel: { aiModel, isUpdate },
    } = this.props;

    if (loading) {
      return <CustomLoading />;
    }

    return (
      <div className='flex w-full flex-col gap-y-5 px-5 py-5 overflow-y-auto max-h-[calc(100dvh-270px)]'>
        <AiModelTrainingParameterComponent
          title={LanguageUtil.get({ id: 'common.label.channel', defaultMessage: 'Channel' })}
          description={LanguageUtil.get({
            id: 'pages.ai.editor.features.channelDescription',
            defaultMessage: 'Determines which transaction data will be used for model training.',
          })}
          input={
            <FormicaSearchSelectBox
              rounded
              disabled={!RoleHelper.canEdit(ComponentName.AI_MODEL) || isUpdate}
              options={this.getTransactionOptions()}
              value={aiModel.channel}
              onChange={this.onChangeChannel}
            />
          }
        />
        <div className='flex h-full flex-col gap-5'>
          <div className='flex h-full flex-col gap-5'>
            <div className='flex w-full items-center justify-between'>
              <span className='font-bold text-primary'>
                {LanguageUtil.get({ id: 'pages.ai.editor.features.baseFeatures.title', defaultMessage: 'Base Features' })}
              </span>
              <span>
                <TableColumnPreferencesComponent
                  items={this.getTransactionVariableOptions()}
                  selectedItems={selectedVariables.map((v) => ({ value: v, label: v }))}
                  onChangeSelectedItems={(e: FormicaSelectBoxItem[] | undefined) => {
                    this.setState({ selectedVariables: (e ?? []).map((v) => v.value) });
                  }}
                  containerNode={(callback: (e: React.MouseEvent) => void) => {
                    return (
                      <span className={'text-md text-f-purple cursor-pointer'} onClick={(e) => callback(e)}>
                        {LanguageUtil.get({
                          id: 'pages.ai.editor.features.baseFeatures.add',
                          defaultMessage: '+ Add base features',
                        })}
                      </span>
                    );
                  }}
                  apply={this.applySelectedVariables}
                />
              </span>
            </div>
            <div className={'flex w-full h-full overflow-scroll items-center justify-start'}>
              <AiModelOriginalFeaturesComponent incrementUpdate={() => this.setState({ update: this.state.update + 1 })} />
            </div>
          </div>

          <div className='flex flex-col gap-[12px]'>
            <div className='flex w-full items-center justify-between'>
              <span className='font-bold text-primary'>
                {LanguageUtil.get({ id: 'pages.ai.editor.features.customFeatures.title', defaultMessage: 'Custom Features' })}
              </span>
              <span>
                <span className={'text-md text-f-purple cursor-pointer'} onClick={this.addCustomFeature}>
                  {LanguageUtil.get({ id: 'pages.ai.editor.features.customFeatures.add', defaultMessage: '+ Add custom features' })}
                </span>
              </span>
            </div>
            <div className={'flex w-full h-full overflow-scroll items-center justify-start'}>
              <AiModelCustomFeaturesComponent update={update} disabled={!RoleHelper.canEdit(ComponentName.AI_MODEL)} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    const {
      aiModel: { aiModel },
    } = this.props;
    this.startTracker();
  }

  public componentWillUnmount() {
    this.stopTracker();
  }

  private startTracker = () => {
    const transactionSubscriptions = this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.TRANSACTIONS);
    this.startEnrichmentTracker();
    this.computation = subscribe.Tracker.autorun(this.tracker, [...transactionSubscriptions, this.enrichmentSubscription!]);
  };

  private tracker = async () => {
    if (this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.TRANSACTIONS).some((s) => s.ready()) && this.enrichmentSubscription?.ready()) {
      await this.buildData();
    }
  };

  private stopTracker = () => {
    this.computation?.stop();
    this.enrichmentSubscription?.stop();
  };

  private buildData = async () => {
    const {
      aiModel: { aiModel },
    } = this.props;
    const transactionQuery = QueryBuilder.select().from(CollectionNames.TRANSACTIONS).build();
    const transactions: TransactionJsonModel[] = await this.repository.findMany(transactionQuery);
    const selectedTransaction: TransactionJsonModel | undefined = transactions.find((t) => t.name == aiModel.channel);
    await this.loadFilters();
    this.setState({ transactions, selectedTransaction, loading: false });
  };

  private loadFilters = async () => {
    const {
      aiModel: { aiModel },
    } = this.props;
    await FilterUtil.getMLOpsDatasourceFields(aiModel.channel, aiModel.baseFeatures, 'TRAINING', false);
    await FilterUtil.getMLOpsDatasourceFields(aiModel.channel, aiModel.baseFeatures, 'VALIDATION', false);
  };

  private getTransactionVariableOptions = (): FormicaSelectBoxItem[] => {
    const { selectedTransaction } = this.state;
    const {
      aiModel: { aiModel },
    } = this.props;
    if (selectedTransaction == undefined) return [];
    return selectedTransaction.variables
      .filter(
        (v) =>
          v.properties.dataType == DataType.NUMBER ||
          v.properties.dataType == DataType.STRING ||
          v.properties.dataType == DataType.BOOLEAN ||
          v.properties.dataType == DataType.DATE,
      )
      .filter((v) => !aiModel.baseFeatures.some((b) => b.name == v.name))
      .map((variable) => {
        return {
          value: variable.name,
          label: variable.name,
        };
      });
  };

  private applySelectedVariables = async () => {
    const {
      aiModel: { aiModel },
      changeAiModel,
    } = this.props;
    const { selectedVariables, selectedTransaction } = this.state;
    selectedVariables.forEach((v) => {
      if (aiModel.baseFeatures.find((f) => f.name == v) != undefined) return;
      const properties = selectedTransaction?.variables.find((variable) => variable.name == v)?.properties;
      if (properties == undefined) return;
      const feature: MLOpsModelBaseFeature = {
        name: v,
        properties,
        description: '',
        enabled: true,
      };
      aiModel.baseFeatures.push(feature);
    });
    changeAiModel(aiModel);
    await FilterUtil.getMLOpsDatasourceFields(aiModel.channel, aiModel.baseFeatures, 'TRAINING', true);
    await FilterUtil.getMLOpsDatasourceFields(aiModel.channel, aiModel.baseFeatures, 'VALIDATION', true);
    this.setState({ selectedVariables: [], update: this.state.update + 1 });
  };

  private addCustomFeature = () => {
    const {
      aiModel: { aiModel },
      changeAiModel,
    } = this.props;
    if (aiModel.customFeatures.length == 0) {
      this.startEnrichmentTracker();
    }
    const expression = ValueFactory.initializeExpression();
    const feature: MLOpsModelCustomFeature = {
      name: 'custom',
      properties: {
        dataType: DataType.STRING,
      },
      description: '',
      expression: expression,
    };
    aiModel.customFeatures.push(feature);
    changeAiModel(aiModel);
  };

  private onChangeChannel = async (e: FormicaSelectBoxItem) => {
    const {
      aiModel: { aiModel },
      changeAiModel,
    } = this.props;
    const { transactions } = this.state;
    aiModel.channel = e.value;
    changeAiModel(aiModel);

    await this.loadFilters();
    const selectedTransaction = transactions.find((t) => t.name == e.value);
    this.setState({ selectedTransaction });
  };

  private getTransactionOptions = (): FormicaSelectBoxItem[] => {
    const { transactions } = this.state;
    return transactions.map((transaction) => {
      return {
        value: transaction.name,
        label: transaction.name,
      };
    });
  };

  private startEnrichmentTracker = () => {
    const {
      aiModel: { aiModel },
    } = this.props;
    const enrichmentSubscriptionQuery = QueryBuilder.select()
      .from(CollectionNames.ENRICHMENTS)
      .where(
        or(and(eq('type', VariableType.LIST), eq('transaction', aiModel.channel)), and(eq('type', VariableType.VELOCITY), eq('transaction', aiModel.channel))),
      )
      .build();
    this.enrichmentSubscription = subscribe.Formica.subscribe(CollectionNames.ENRICHMENTS, enrichmentSubscriptionQuery);
  };
}

class State {
  transactions: TransactionJsonModel[] = [];
  selectedTransaction: TransactionJsonModel | undefined;
  loading: boolean = true;

  selectedVariables: string[] = [];
  update: number = 0;
}

interface Props extends ReduxProps, FormicaProps {}

const mapStateToProps = (state: RootState) => ({
  aiModel: state.aiModel as AiModelState,
  isUpdate: state.aiModel.isUpdate,
});
const mapDispatchToProps = {
  changeAiModel,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(AiModelFeaturesComponent));
